import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStrings } from '../i18n';
import useOrderPolling from '../hooks/useOrderPolling';
import ShipmentStatus from '../components/ShipmentStatus';
import PanchTatvaButton from '../components/PanchTatvaButton';

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getIdToken, userInfo, isAuthenticated } = useAuth();
  const strings = useStrings();
  const oc = strings.orderConfirmation;
  // Seed from navigation state (populated synchronously by Checkout.jsx)
  const [order, setOrder] = useState(location.state?.order || null);
  // Guest email passed from Checkout when not authenticated
  const guestEmail = location.state?.guestEmail || null;

  // Redirect if arrived with no order context
  React.useEffect(() => {
    if (!order) navigate('/');
  }, [order, navigate]);

  // Polling — enabled while shipment_id is absent
  const hasShipment = Boolean(order?.shiprocket?.shipment_id);
  const pollingEnabled = isAuthenticated && !hasShipment && Boolean(orderId);

  const { isPolling, pollingSeconds } = useOrderPolling(orderId, null, {
    enabled: pollingEnabled,
    getIdToken: isAuthenticated ? getIdToken : null,
    onShipmentReady: (updatedOrder) => setOrder(updatedOrder),
    onPermanentFail: (updatedOrder) => setOrder(updatedOrder),
  });

  if (!order) return null;

  const orderDate = new Date(order.createdAt).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const addr = order.shippingAddress || {};

  const expectedDelivery = new Date();
  if (order.shippingMethod === 'express') {
    expectedDelivery.setDate(expectedDelivery.getDate() + 2);
  } else {
    expectedDelivery.setDate(expectedDelivery.getDate() + 5);
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
      {/* Success Banner */}
      <div className="mb-8 md:mb-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-100 mb-4">
          <svg
            className="w-8 h-8 md:w-10 md:h-10 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-2">
          {oc.title}
        </h1>
        <p className="font-sans text-bodyText text-lg">{oc.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ── Main Content ─────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order meta */}
          <div className="bg-cardBg rounded-2xl shadow-card p-6 md:p-8">
            <h2 className="text-2xl font-heading text-headingText mb-6">{oc.title}</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 pb-6 border-b border-surface">
              <div>
                <p className="font-sans text-sm text-textSecondary mb-1">{oc.orderId}</p>
                <p className="font-sans text-base font-semibold text-bodyText font-mono">{orderId}</p>
              </div>
              <div>
                <p className="font-sans text-sm text-textSecondary mb-1">{oc.orderDate}</p>
                <p className="font-sans text-base font-semibold text-bodyText">{orderDate}</p>
              </div>
              <div>
                <p className="font-sans text-sm text-textSecondary mb-1">{oc.total}</p>
                <p className="font-sans text-base font-semibold text-bodyText">₹{order.pricing?.total}</p>
              </div>
            </div>

            {/* Items */}
            <h3 className="text-lg font-heading text-headingText mb-4">{oc.items}</h3>
            <div className="space-y-3 mb-6 pb-6 border-b border-surface">
              {(order.items || []).map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="flex items-center justify-between p-3 bg-primaryBg rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-12 h-12 md:w-16 md:h-16 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <p className="font-sans font-semibold text-bodyText">{item.name}</p>
                      <p className="font-sans text-sm text-textSecondary">
                        {strings.orderDetails.quantity}: {item.quantity}
                      </p>
                    </div>
                  </div>
                  <p className="font-sans font-semibold text-bodyText">₹{item.price * item.quantity}</p>
                </div>
              ))}
            </div>

            {/* Shipping Address */}
            <h3 className="text-lg font-heading text-headingText mb-4">{oc.shippingAddress}</h3>
            <div className="bg-primaryBg rounded-xl p-4 mb-6 pb-6 border-b border-surface">
              <p className="font-sans font-semibold text-bodyText mb-1">{addr.fullName}</p>
              <p className="font-sans text-sm text-bodyText">{addr.phone}</p>
              <p className="font-sans text-sm text-bodyText">{addr.address}</p>
              <p className="font-sans text-sm text-bodyText">
                {addr.city}, {addr.state} – {addr.pincode}
              </p>
            </div>

            {/* Price Summary */}
            <h3 className="text-lg font-heading text-headingText mb-4">{oc.priceSummary}</h3>
            <div className="space-y-3 p-4 bg-primaryBg rounded-xl">
              <div className="flex justify-between font-sans text-bodyText">
                <span>{oc.subtotal}</span>
                <span>₹{order.pricing?.subtotal}</span>
              </div>
              <div className="flex justify-between font-sans text-bodyText">
                <span>{oc.shipping}</span>
                <span>{order.pricing?.shipping === 0 ? oc.free : `₹${order.pricing?.shipping}`}</span>
              </div>
              <div className="flex justify-between font-sans text-bodyText">
                <span>{oc.tax}</span>
                <span>₹{order.pricing?.tax}</span>
              </div>
              <div className="flex justify-between font-sans text-lg font-bold text-secondary pt-3 border-t border-surface">
                <span>{oc.total}</span>
                <span>₹{order.pricing?.total}</span>
              </div>
            </div>
          </div>

          {/* Confirmation email notice — authenticated user */}
          {isAuthenticated && userInfo?.email && (
            <div className="bg-blue-50 border-l-4 border-secondary p-5 rounded-xl">
              <h3 className="font-sans font-semibold text-blue-900 mb-1">{oc.confirmationEmail}</h3>
              <p className="font-sans text-sm text-blue-800">
                {oc.confirmationEmailDesc} <strong>{userInfo.email}</strong>
              </p>
            </div>
          )}

          {/* Confirmation email notice — guest */}
          {!isAuthenticated && guestEmail && (
            <div className="bg-blue-50 border-l-4 border-secondary p-5 rounded-xl">
              <h3 className="font-sans font-semibold text-blue-900 mb-1">{oc.confirmationEmail}</h3>
              <p className="font-sans text-sm text-blue-800">
                {oc.confirmationEmailDesc} <strong>{guestEmail}</strong>
              </p>
            </div>
          )}

          {/* Guest sign-up prompt — only shown to unauthenticated users */}
          {!isAuthenticated && (
            <div className="bg-cardBg border border-secondary/30 rounded-2xl p-6">
              <h3 className="font-heading text-lg text-headingText mb-2">
                {oc.guestPrompt || 'Create an account to track this order'}
              </h3>
              <p className="font-sans text-sm text-textSecondary mb-4">
                {oc.guestPromptDesc || 'Sign up to view your order history, track deliveries, and reorder with one click.'}
              </p>
              <Link
                to="/login"
                state={{ from: `/order-confirmation/${orderId}` }}
              >
                <PanchTatvaButton variant="outline">
                  {oc.guestPromptCta || 'Create Account'}
                </PanchTatvaButton>
              </Link>
            </div>
          )}
        </div>

        {/* ── Sidebar ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-1">
          <div className="sticky top-32 space-y-6">
            {/* Live shipment status (replaces static "What's Next?") */}
            <ShipmentStatus
              order={order}
              isPolling={isPolling}
              pollingSeconds={pollingSeconds}
              onContactSupport={() => navigate('/contact')}
            />

            {/* Progress steps */}
            <div className="bg-cardBg rounded-2xl shadow-card p-6">
              <h3 className="text-xl font-heading text-headingText mb-5">{oc.whatsNext}</h3>
              <ol className="space-y-4">
                {[oc.orderConfirmedStep, oc.preparingStep, oc.shippedStep, oc.deliveredStep].map(
                  (label, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold font-sans ${
                          i === 0
                            ? 'bg-secondary text-primaryBg'
                            : 'bg-surface text-textSecondary'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="font-sans text-sm text-bodyText pt-0.5">{label}</span>
                    </li>
                  )
                )}
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-12 flex flex-col md:flex-row gap-4 justify-center">
        {isAuthenticated && (
          <Link to="/orders">
            <PanchTatvaButton variant="outline">{oc.viewOrderHistory}</PanchTatvaButton>
          </Link>
        )}
        {isAuthenticated && (
          <Link to={`/orders/${orderId}/receipt`}>
            <PanchTatvaButton variant="outline">{oc.viewReceipt}</PanchTatvaButton>
          </Link>
        )}
        <Link to="/shop">
          <PanchTatvaButton variant="primary">{oc.continueShopping}</PanchTatvaButton>
        </Link>
      </div>
    </div>
  );
}
